<!-- eslint-disable vue/no-v-html -->
<template>
  <transition-group name="fade" tag="div" class="email d-flex flex-column px-3 mb-5">
    <section
      v-if="!(repEmailData || fetching)"
      key="error"
      class="d-flex flex-column justify-center align-center"
      style="min-height: 240px"
    >
      <span class="text-h2 mb-5">{{ $t('action.errors.oops') }}</span>
      <span class="text-center" v-html="$t('action.errors.generic')" />
    </section>
    <section v-if="repEmailData" key="d-none container">
      <section class="d-flex flex-column align-center mb-6">
        <div class="text-h6 text-center font-weight-bold mb-3">
          {{ $t('action.panels.LTT_EMAIL.title', locale, { repName, stepNumber })
          }}<b v-if="repParty"> ({{ repParty }})</b>!
        </div>
        <div class="text-subtitle-1 text-center">
          {{ $t('action.panels.LTT_EMAIL.description', locale) }}
        </div>
      </section>

      <v-tabs v-model="tab" centered icons-and-text>
        <v-tabs-slider />

        <v-tab :key="1">
          {{ $t('action.panels.LTT_EMAIL.option1TabTitle', locale) }}
          <v-icon>{{ icons.mdiEmailSendOutline }}</v-icon>
        </v-tab>

        <v-tab :key="2">
          {{ $t('action.panels.LTT_EMAIL.option2TabTitle', locale) }}
          <v-icon>{{ icons.mdiDownloadBoxOutline }}</v-icon>
        </v-tab>

        <v-tab :key="3">
          {{ $t('action.panels.LTT_EMAIL.option3TabTitle', locale) }}
          <v-icon>{{ icons.mdiContentCopy }}</v-icon>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item :key="1">
          <v-card flat>
            <v-card-text class="d-flex flex-column align-center">
              <div class="text-h5 text-center mb-2">
                {{ $t('action.panels.LTT_EMAIL.option1Title', locale) }}
              </div>
              <div class="text-subtitle-2 text-center mb-2">
                {{ $t('action.panels.LTT_EMAIL.option1Description', locale) }}
              </div>
              <v-btn
                class="align-self-center mb-2"
                color="primary"
                target="_blank"
                :href="mailtoHref"
                outlined
                @click="$store.dispatch('actionForm/changeToNextPanel')"
              >
                <v-icon>{{ icons.mdiEmailSendOutline }}</v-icon>
                &nbsp;
                {{ $t('action.ltt.buttons.option1.label', locale) }}
              </v-btn>
              <div class="text-caption text-center">
                {{ $t('action.panels.LTT_EMAIL.option1Tip', locale) }}
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item :key="2">
          <v-card flat>
            <v-card-text class="d-flex flex-column align-center">
              <div class="text-h5 text-center mb-2">
                {{ $t('action.panels.LTT_EMAIL.option2Title', locale) }}
              </div>
              <div class="text-subtitle-2 text-center mb-2">
                {{ $t('action.panels.LTT_EMAIL.option2Description', locale) }}
              </div>
              <form
                class="d-flex justify-center w-100"
                method="post"
                :action="emlDownloadUrl"
                @submit="$store.dispatch('actionForm/changeToNextPanel')"
              >
                <input type="hidden" name="first_name" :value="senderFirstName" />
                <input type="hidden" name="last_name" :value="senderLastName" />
                <input type="hidden" name="bcc" :value="bcc" />
                <v-btn color="primary" type="submit" :disabled="!emlDownloadUrl" outlined>
                  <v-icon>{{ icons.mdiDownloadBoxOutline }}</v-icon>
                  &nbsp;
                  {{ $t('action.ltt.buttons.option2.label', locale) }}
                </v-btn>
              </form>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item :key="3">
          <v-card flat>
            <v-card-text class="d-flex flex-column">
              <div class="text-h5 align-self-start mb-2">
                {{ $t('action.panels.LTT_EMAIL.option3Title', locale) }}
              </div>
              <div class="text-subtitle-2 mb-3">
                {{ $t('action.panels.LTT_EMAIL.option3Description', locale) }}
              </div>

              <section class="d-flex flex-column mb-6">
                <div class="font-weight-bold mb-2">
                  {{ $t('action.panels.LTT_EMAIL.repEmail', locale, { repName }) }}
                </div>
                <v-text-field
                  ref="repEmail"
                  v-model="repEmail"
                  class="monospace mb-2"
                  solo
                  readonly
                  hide-details
                />
                <v-btn
                  class="align-self-start"
                  color="primary"
                  outlined
                  @click="handleCopyButtonClick('repEmail')"
                >
                  <v-icon>{{ icons.mdiContentCopy }}</v-icon>
                  &nbsp;
                  {{ copyEmailLabel }}
                </v-btn>
              </section>

              <section class="d-flex flex-column mb-6">
                <div class="font-weight-bold mb-2">
                  {{ $t('action.panels.LTT_EMAIL.subject', locale) }}
                </div>
                <v-text-field
                  ref="subject"
                  v-model="subject"
                  class="monospace mb-2"
                  solo
                  readonly
                  hide-details
                />
                <v-btn
                  class="align-self-start"
                  color="primary"
                  outlined
                  @click="handleCopyButtonClick('subject')"
                >
                  <v-icon>{{ icons.mdiContentCopy }}</v-icon>
                  &nbsp;
                  {{ copySubjectLabel }}
                </v-btn>
              </section>

              <section class="d-flex flex-column mb-6">
                <div class="font-weight-bold mb-2">
                  {{ $t('action.panels.LTT_EMAIL.body', locale) }}
                </div>
                <v-textarea
                  ref="body"
                  v-model="body"
                  class="monospace mb-2"
                  full-width
                  auto-grow
                  solo
                  readonly
                  hide-details
                />
                <v-btn
                  class="align-self-start"
                  color="primary"
                  outlined
                  @click="handleCopyButtonClick('body')"
                >
                  <v-icon>{{ icons.mdiContentCopy }}</v-icon>
                  &nbsp;
                  {{ copyBodyLabel }}
                </v-btn>
              </section>

              <section class="mb-6">
                <div class="mb-3">
                  {{ $t('action.panels.LTT_EMAIL.clickContinue', locale) }}
                </div>
                <v-btn
                  color="secondary"
                  small
                  @click="$store.dispatch('actionForm/changeToNextPanel')"
                >
                  <v-icon>{{ icons.mdiArrowRightBoldCircleOutline }}</v-icon>
                  &nbsp;
                  {{ $t('action.ltt.buttons.continue.label', locale) }}
                </v-btn>
              </section>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </section>
  </transition-group>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
  mdiContentCopy,
  mdiEmailSendOutline,
  mdiDownloadBoxOutline,
  mdiArrowRightBoldCircleOutline
} from '@mdi/js'

export default {
  name: 'PanelLttEmail',
  data: () => ({
    emailData: {},
    baseUrl: process.env.petitionsBaseUrl || process.env.PETITIONS_BASE_DOMAIN || '',
    tab: null,
    icons: {
      mdiContentCopy,
      mdiEmailSendOutline,
      mdiDownloadBoxOutline,
      mdiArrowRightBoldCircleOutline
    },
    lastCopied: null,
    lastCopyError: null
  }),
  computed: {
    repName() {
      return this.repEmailData ? this.repEmailData.full_name : ''
    },
    repParty() {
      return this.repEmailData ? this.repEmailData.party : ''
    },
    repEmail() {
      return this.repEmailData ? this.repEmailData.email : ''
    },
    bcc() {
      // Multiple BCC and CC addresses should be separated with a semicolon, apparently.
      // https://stackoverflow.com/a/26509927
      return this.repEmailData && this.repEmailData.bcc && this.repEmailData.bcc.length
        ? this.repEmailData.bcc.map(bcc => encodeURIComponent(bcc)).join(';')
        : ''
    },
    subject() {
      return this.repEmailData ? this.repEmailData.subject : ''
    },
    body() {
      return this.repEmailData && this.repEmailData.body ? `${this.repEmailData.body}\r\n` : ''
    },
    emlDownloadUrl() {
      return this.repEmailData && this.repEmailData.eml_download_url
        ? `${this.baseUrl}${this.repEmailData.eml_download_url}`
        : ''
    },
    senderFirstName() {
      return this.postData ? this.postData.first_name : ''
    },
    senderLastName() {
      return this.postData ? this.postData.last_name : ''
    },
    senderFullName() {
      return `${this.senderFirstName} ${this.senderLastName}`
    },
    stepNumber() {
      return this.isSingleRepLTT ? 2 : 3
    },
    mailtoHref() {
      const { subject, body, bcc } = this
      const params = {
        subject,
        body
      }
      // Only add the BCC if the privacy agreement allows it, and its present
      if (bcc && !this.optOutChosen) {
        params.bcc = bcc
      }
      const encodedParams = Object.entries(params)
        .map(([key, value]) => {
          if (key === 'bcc') {
            // Don't encode the value if it is bcc, we want to keep any semi-colon separators, not
            // encode them
            return `${encodeURIComponent(key)}=${value}`
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        })
        .join('&')
      return `mailto:${this.repEmail}?${encodedParams}`
    },
    copyEmailLabel() {
      return this.getButtonCopy('repEmail')
    },
    copySubjectLabel() {
      return this.getButtonCopy('subject')
    },
    copyBodyLabel() {
      return this.getButtonCopy('body')
    },
    ...mapState('actionForm', ['actionId', 'fetching', 'locale', 'repEmailData', 'postData']),
    ...mapGetters('actionForm', ['optOutChosen', 'isSingleRepLTT'])
  },
  methods: {
    handleCopyButtonClick(ref) {
      this.$refs[ref].$refs.input.select()
      try {
        document.execCommand('copy')
        this.lastCopied = ref
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        this.lastCopyError = ref
      }
    },
    getButtonCopy(ref) {
      if (this.lastCopyError === ref) {
        return this.$t('action.ltt.buttons.copyManual.label', this.locale)
      } else if (this.lastCopied === ref) {
        return this.$t('action.ltt.buttons.copied.label', this.locale)
      }
      switch (ref) {
        case 'repEmail':
          return this.$t('action.ltt.buttons.copyEmail.label', this.locale)
        case 'subject':
          return this.$t('action.ltt.buttons.copySubject.label', this.locale)
        case 'body':
          return this.$t('action.ltt.buttons.copyBody.label', this.locale)
      }
      return this.$t('action.ltt.buttons.copy.label', this.locale)
    }
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

::v-deep .v-card__text {
  color: unset !important;
}
</style>
